import { apiClient, getCsrfToken } from './api';
import { API_BASE_URL } from '../config/apiBase';
import type { WebhookGroup, DiscordSendResponse, BotIdentity } from '../types/discordComposer';

// Response may be array directly, or wrapped in object (paginated or otherwise)
interface WebhookGroupsResponse {
  results?: WebhookGroup[];
  data?: WebhookGroup[];
  webhook_groups?: WebhookGroup[];
}

/**
 * Discord's own per-file ceiling is 20 MiB for non-boosted uploads. Enforcing it
 * client-side turns an opaque server rejection into an immediate, specific
 * message naming the offending file.
 */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;

/**
 * Total multipart budget. Sits under the proxy's 25m client_max_body_size so the
 * user is told what is wrong before an upload that nginx would reject anyway.
 */
export const MAX_TOTAL_UPLOAD_BYTES = 24 * 1024 * 1024;

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Build a human-readable error from a failed response.
 *
 * The body is read as text exactly once: a failure here is not guaranteed to be
 * our JSON envelope (nginx emits HTML, a proxy may emit plain text, a dead
 * worker emits nothing), and calling .json() first would consume the stream and
 * make a text fallback impossible.
 */
async function describeErrorResponse(response: Response): Promise<string> {
  let body = '';
  try {
    body = await response.text();
  } catch {
    // Ignore — fall through to the status-only message below.
  }

  if (body) {
    try {
      const parsed = JSON.parse(body);
      const message = parsed?.error?.message ?? parsed?.detail ?? parsed?.message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    } catch {
      // Not JSON (HTML error page, plain text). Fall through.
    }
  }

  return `Failed to send message (HTTP ${response.status} ${response.statusText})`.trim();
}

export const discordComposerService = {
  /**
   * Get available webhook groups
   * GET /api/jdash/discord-composer/webhook-groups/
   */
  async getWebhookGroups(): Promise<WebhookGroup[]> {
    const response = await apiClient.get<WebhookGroup[] | WebhookGroupsResponse>(
      '/api/jdash/discord-composer/webhook-groups/'
    );

    // Handle array directly
    if (Array.isArray(response)) {
      return response;
    }

    // Handle paginated/wrapped response
    if (response && typeof response === 'object') {
      if (Array.isArray(response.results)) return response.results;
      if (Array.isArray(response.data)) return response.data;
      if (Array.isArray(response.webhook_groups)) return response.webhook_groups;
    }

    // Log unexpected format and return empty array
    console.error('Unexpected webhook groups response format:', response);
    return [];
  },

  async getBotIdentities(): Promise<BotIdentity[]> {
    const response = await apiClient.get<BotIdentity[] | { data?: BotIdentity[] }>(
      '/api/jdash/discord-composer/bot-identities/'
    );

    if (Array.isArray(response)) {
      return response;
    }

    if (response && typeof response === 'object') {
      if (Array.isArray(response.data)) return response.data;
    }

    console.error('Unexpected bot identities response format:', response);
    return [];
  },

  async sendMessage(
    webhookGroupId: number,
    content: string,
    files?: FileList | null,
  ): Promise<DiscordSendResponse> {
    const formData = new FormData();
    formData.append('webhook_group_id', webhookGroupId.toString());
    formData.append('content', content);

    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        formData.append('files', file);
      });
    }

    // Raw fetch (not apiClient) so the browser sets the multipart boundary for
    // FormData. Auth rides the session cookie (credentials: 'include'); the
    // X-CSRFToken header is supplied manually since axios isn't doing it here.
    const response = await fetch(`${API_BASE_URL}/api/jdash/discord-composer/send/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-CSRFToken': getCsrfToken() ?? '',
        // Don't set Content-Type - let browser set it with boundary for FormData
      },
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('You do not have permission to send Discord messages');
      }

      // A 413 is returned by nginx, not Django, so the body is HTML rather than
      // our APIResponse envelope. Special-case it: the generic reader below
      // would surface a wall of markup.
      if (response.status === 413) {
        throw new Error(
          `Attachments are too large to upload (max ${formatBytes(MAX_TOTAL_UPLOAD_BYTES)} total). Remove or shrink some files and try again.`,
        );
      }

      // Previously this threw a fixed 'Failed to send message' and discarded the
      // body unread, which is why a deterministic 413 looked random for weeks.
      // Always surface what the server actually said.
      throw new Error(await describeErrorResponse(response));
    }

    return response.json();
  },
};
